/**
 * Planning Engine Module — TypeScript types
 * v21.1: Global Planning Mode, Item Planning Basis, Decomposition Maturity
 * v22:   Team Participation Requirements
 */

// ---------------------------------------------------------------------------
// Planning Mode (global, set on Scenario)
// ---------------------------------------------------------------------------

export type PlanningMode = 'priority_estimate' | 'work_units' | 'hybrid_remaining';

/**
 * A scenario accounts for uncertainty one way or the other, never both — they
 * are two ways of handling the same thing, so both at once double-counts it.
 *
 * 'buffer'    — shrink supply by the scenario's capacity buffer; ignore the
 *               execution recipes' per-work-type multipliers.
 * 'work_type' — inflate demand by each work type's uncertainty multiplier;
 *               ignore the buffer.
 */
export type UncertaintyBasis = 'buffer' | 'work_type';

export const PLANNING_MODE_LABELS: Record<PlanningMode, string> = {
  priority_estimate: 'Priority Estimate',
  work_units: 'Work Units',
  hybrid_remaining: 'Hybrid / Remaining Estimate',
};

// ---------------------------------------------------------------------------
// Decomposition maturity (set per Priority)
// ---------------------------------------------------------------------------

export type WorkDefinitionStatus = 'not_decomposed' | 'partially_decomposed' | 'fully_decomposed';

export const WORK_DEFINITION_STATUS_LABELS: Record<WorkDefinitionStatus, string> = {
  not_decomposed: 'Not Decomposed',
  partially_decomposed: 'Partially Decomposed',
  fully_decomposed: 'Fully Decomposed',
};

// ---------------------------------------------------------------------------
// Item-level planning basis (set per Priority)
// Controls how this priority is estimated when global mode allows mixed grain
// ---------------------------------------------------------------------------

export type ItemPlanningBasis = 'priority_estimate' | 'work_units' | 'hybrid_remaining';

// ---------------------------------------------------------------------------
// Consistency check
// ---------------------------------------------------------------------------

export type ConsistencyCheckStatus = 'OK' | 'Review' | 'Invalid';

export interface ConsistencyCheck {
  status: ConsistencyCheckStatus;
  message: string;
}

// ---------------------------------------------------------------------------
// Scenario
// ---------------------------------------------------------------------------

export interface Scenario {
  id: string;
  projectId: string;
  /** Optional link to governance plan_cycles.id (set when this scenario is bound to a cycle). */
  planCycleId: string | null;
  name: string;
  active: boolean;
  planningMode: PlanningMode;
  planningWeeks: number;
  capacityBufferPercent: number; // 0–1  e.g. 0.2 = 20%
  /**
   * How this scenario accounts for uncertainty — one way, not two.
   *
   * 'buffer' shrinks supply by capacityBufferPercent and ignores the execution
   * recipes' uncertainty multipliers. 'work_type' applies those multipliers to
   * demand and ignores the buffer. Applying both double-counts the same
   * uncertainty, and applying one on each side of the product made the engine
   * and the plan of record disagree about identical work.
   */
  uncertaintyBasis?: UncertaintyBasis;
  defaultWorkUnitHours: number;
  includeNotReady: boolean;
  includeBlocked: boolean;
  notes: string | null;
  /** Time-phased execution planning horizon */
  horizonStartDate: string | null;
  horizonEndDate: string | null;
  bucketGranularity: 'week' | 'month';
  timePhasedEnabled: boolean;
  /**
   * Bound Engine Input Scope for this scenario (hard container for left-nav lists).
   * Null = unbound free exploration (All Live Items allowed).
   */
  inputSourceType?: string | null;
  inputSourceId?: string | null;
  inputSourceName?: string | null;
  /**
   * Where this scenario's pools come from (v23).
   * 'manual' = stored planning_capacity rows only — the default, forever.
   * 'roster' = active role assignments rolled up per resource type, alongside
   * any manual rows. Absent on rows written before migration 687.
   */
  capacitySource?: CapacitySource;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Priority
// ---------------------------------------------------------------------------

export type PriorityFeasibility = 'Fully Achievable' | 'Partially Achievable' | 'Not Achievable' | 'No Work';

export interface Priority {
  id: string;
  projectId: string;
  scenarioId: string | null;
  name: string;
  category: string | null;
  altitudeBand: string | null;
  track: string | null;
  itemType: string | null;
  source: string | null;
  confidence: number | null;
  strategicObjective: string | null;
  accountableLeader: string | null;
  forceRank: number | null;
  aboveTheLine: boolean | null;
  valueScore: number | null;
  riskReduction: number | null;
  complianceFlag: boolean | null;
  executiveImperative: boolean | null;
  costOfDelay: number | null;
  impactScore: number | null;
  effortScore: number | null;
  cognitiveComplexity: number | null;
  // v21.1 planning maturity fields
  workDefinitionStatus: WorkDefinitionStatus;
  itemPlanningBasis: ItemPlanningBasis;
  priorityLevelWuEstimate: number | null; // parent-level directional estimate
  remainingWuEstimate: number | null;     // explicit judgment — NOT auto-calculated
  // v22.1 resource type for priority-level capacity allocation
  priorityResourceType: string;
  notes: string | null;
  /** Time-phased launch window */
  targetLaunchStart: string | null;
  targetLaunchEnd: string | null;
  launchProfileId: string | null;
  /** Denormalized display label for owning org. */
  owningOrg: string | null;
  /** FK to org_units — preferred over free-text owningOrg. */
  owningOrgUnitId: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// WorkUnit
// ---------------------------------------------------------------------------

export type ResourceType =
  | 'engineering'
  | 'design'
  | 'pm'
  | 'data'
  | 'qa'
  | 'devops'
  | 'research'
  | 'marketing'
  | 'sales'
  | 'support'
  | 'legal'
  | 'finance'
  | 'other'
  | 'unknown';

export type WorkUnitState =
  | 'backlog'
  | 'ready'
  | 'selected_for_dev'
  | 'in_progress'
  | 'done'
  | 'blocked'
  | 'cancelled';

export interface WorkUnit {
  id: string;
  projectId: string;
  priorityId: string | null;
  parentWorkUnitId: string | null;
  title: string;
  description: string | null;
  workProfile: string; // maps to ResourceType but kept as string for flexibility
  requiredCapability: string | null;
  assignedOwner: string | null;
  effortEstimateHours: number;
  durationEstimateDays: number | null;
  countedEffortHours: number | null;
  /**
   * The execution recipe this unit's work type resolves to, carrying the
   * uncertainty multiplier. Populated on the row by the sync path; the
   * multiplier is applied at demand-math time, and only under the 'work_type'
   * uncertainty basis.
   */
  resolvedRecipeId?: string | null;
  state: WorkUnitState;
  assumptionFlag: boolean | null;
  riskLevel: string | null;
  milestone: string | null;
  includedInPlan: boolean | null;
  constraintAdjustedRank: number | null;
  rankDelta: number | null;
  externalIssueKey: string | null;
  sourceId: string | null;
  sourceType: string | null;
  sourceUrl: string | null;
  customerName: string | null;
  requestedBy: string | null;
  requestedByOrg: string | null;
  systemsTouched: unknown | null;
  workflowsTouched: unknown | null;
  personasImpacted: unknown | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Dependency (work-unit level)
// ---------------------------------------------------------------------------

export type DependencyType = 'Hard' | 'Soft';

export interface Dependency {
  id: string;
  projectId: string;
  fromWorkUnitId: string; // the work unit that depends on something
  toWorkUnitId: string;   // the prerequisite that must be done first
  dependencyType: DependencyType;
  blocking: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Priority-level dependency (v21.1)
// ---------------------------------------------------------------------------

export interface PriorityDependency {
  id: string;
  projectId: string;
  fromPriorityId: string;
  toPriorityId: string;
  dependencyType: DependencyType;
  blocking: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// CapacityModelRow (work-unit level)
// ---------------------------------------------------------------------------

export type CapacitySource = 'manual' | 'roster';

export interface CapacityModelRow {
  id: string;
  projectId: string;
  scenarioId: string;
  resourceType: string;
  resourceName: string | null;
  weeklyCapacityHours: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  // Derived — not stored in DB
  effectiveCapacityHours: number;
  workUnitCapacity: number;
  /**
   * Provenance. Absent (or 'manual') for a stored row; 'roster' for a pool
   * derived from role assignments at run time — see planningRoster.ts.
   */
  source?: CapacitySource;
}

// ---------------------------------------------------------------------------
// Priority-level capacity model (v21.1)
// ---------------------------------------------------------------------------

export interface PriorityCapacityRow {
  id: string;
  projectId: string;
  scenarioId: string;
  resourceType: string;
  resourceName: string | null;
  weeklyCapacityHours: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  // Derived — not stored in DB
  effectiveCapacityHours: number;
  workUnitCapacity: number;
  source?: CapacitySource;
}

// ---------------------------------------------------------------------------
// Team Participation Requirements (v22)
// ---------------------------------------------------------------------------

export type ParticipationTristate = 'yes' | 'no' | 'partial';

/** Period-grain participation lifecycle (Keith / shared-team confirm). */
export type ParticipationRequestStatus =
  | 'requested'
  | 'tentative'
  | 'confirmed'
  | 'declined';

export interface ParticipationPeriodCommitment {
  /** ISO date — typically week start */
  bucketStart: string;
  bucketEnd?: string;
  hours?: number | null;
  status: ParticipationRequestStatus;
}

export interface TeamParticipation {
  id: string;
  projectId: string;
  priorityId: string | null;
  workUnitId: string | null;
  /** Display label for the counterpart. Denormalized from the org unit when `requiredOrgUnitId` is set. */
  requiredTeam: string;
  /**
   * Hierarchy node (org unit) this requirement asks participation from
   * (migration 719, brief HIERARCHY-TYPES §8 Phase 1). null = legacy text-only
   * row that no org unit matched; the picker links it.
   */
  requiredOrgUnitId: string | null;
  requiredCapability: string | null;
  capacityRequiredWu: number | null;
  capacityConfirmed: ParticipationTristate;
  capabilityPresent: ParticipationTristate;
  committed: ParticipationTristate;
  /** Display label for the accountable contact. Denormalized from the user when `ownerUserId` is set. */
  owner: string | null;
  /** Accountable contact as a user reference (migration 719). Distinct from `participationOwnerId`, who ANSWERS the request. */
  ownerUserId: string | null;
  riskIfUnavailable: string | null;
  notes: string | null;
  committedWindowStart: string | null;
  committedWindowEnd: string | null;
  participationOwnerId: string | null;
  participationDeadline: string | null;
  /** Overall request lifecycle; period_commitments refine per bucket. */
  requestStatus: ParticipationRequestStatus;
  periodCommitments: ParticipationPeriodCommitment[];
  /**
   * Hand-off provenance (v23, Part D). `requestedAt` null = still a draft the
   * requester owns outright; once sent, only the responding side may change
   * requestStatus.
   */
  requestedBy: string | null;
  requestedAt: string | null;
  respondedBy: string | null;
  respondedAt: string | null;
  responseNote: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Participation-aware status types (v22)
// ---------------------------------------------------------------------------

export type ParticipationCapacityStatus =
  | 'Confirmed'
  | 'Partially Confirmed'
  | 'Not Confirmed'
  | 'No Requirements';

export type CapabilityStatus =
  | 'Present'
  | 'Partial'
  | 'Missing'
  | 'No Requirements';

export type CommitmentStatus =
  | 'Committed'
  | 'Partially Committed'
  | 'Not Committed'
  | 'No Requirements';

export type DecisionWithParticipation =
  | 'Included'
  | 'At Risk - Participation'
  | 'At Risk - Capacity'
  | 'Blocked - Capability'
  | 'Out - capacity'
  | 'Deferred - No Qualified Capacity'
  | 'Blocked'
  | 'Not Ready'
  | 'Needs Sizing'
  | 'No Capacity Model'
  | 'No Work Units Defined';

// ---------------------------------------------------------------------------
// Demand calculations (v21.1)
// ---------------------------------------------------------------------------

// What the item's own planning basis implies
export interface ItemDemand {
  itemPlanningDemand: number | null;  // null = cannot be calculated
  identifiedWorkUnitsTotal: number;   // sum of child work-unit estimates
}

// ---------------------------------------------------------------------------
// Plan record (work-unit grain)
// ---------------------------------------------------------------------------

export type DependencyStatus = 'Satisfied' | 'Unsatisfied' | 'No Dependencies';
export type ReadinessStatus = 'Ready' | 'Not Ready';
export type CapacityStatus =
  | 'Has Capacity'
  | 'No Capacity'
  | 'No Capacity Model'
  /** The pool has room; the slice of it that qualifies for this work does not. */
  | 'No Qualified Capacity';

export type PlanDecision =
  | 'Included'
  | 'Out - capacity'
  /**
   * The moment the engine's explanation reaches staffing-conversation
   * resolution: capacity exists in the pool, but not held by anyone who meets
   * this work's capability / credential / named-person requirements.
   */
  | 'Deferred - No Qualified Capacity'
  | 'Blocked'
  | 'Not Ready'
  | 'Needs Sizing'
  | 'No Capacity Model'
  | 'No Work Units Defined';

export interface PlanRecord {
  workUnit: WorkUnit;
  priority: Priority | null;
  workUnitsRequired: number;
  dependencyStatus: DependencyStatus;
  readinessStatus: ReadinessStatus;
  capacityStatus: CapacityStatus;
  planDecision: PlanDecision;
  decisionReason: string;
  runningDemandBefore: number;
  runningDemandAfter: number;
  // v22 participation fields
  participationRequirementCount: number;
  participationCapacityRequiredWu: number;
  participationCapacityStatus: ParticipationCapacityStatus;
  capabilityStatus: CapabilityStatus;
  commitmentStatus: CommitmentStatus;
  participationRisk: string | null;
  decisionWithParticipation: DecisionWithParticipation;
}

// ---------------------------------------------------------------------------
// Priority plan record (priority-level grain, v21.1)
// ---------------------------------------------------------------------------

export interface PriorityPlanRecord {
  priority: Priority;
  // v21.1 demand calculation
  identifiedWorkUnitsTotal: number;
  itemPlanningDemand: number | null;
  activeModeplanningDemand: number | null;
  // consistency check
  consistencyCheck: ConsistencyCheck;
  // decision
  dependencyStatus: DependencyStatus;
  /**
   * Why the capacity check landed where it did. Optional: priority-grain
   * records written before qualified capacity shipped do not carry it.
   */
  capacityStatus?: CapacityStatus;
  planDecision: PlanDecision;
  decisionReason: string;
  runningDemandBefore: number;
  runningDemandAfter: number;
  // v22 participation
  participationRequirementCount: number;
  participationCapacityRequiredWu: number;
  participationCapacityStatus: ParticipationCapacityStatus;
  capabilityStatus: CapabilityStatus;
  commitmentStatus: CommitmentStatus;
  participationRisk: string | null;
  decisionWithParticipation: DecisionWithParticipation;
}

// ---------------------------------------------------------------------------
// Priority / Resource summaries
// ---------------------------------------------------------------------------

export interface PrioritySummary {
  priority: Priority;
  requiredWU: number;
  includedWU: number;
  deferredWU: number;
  blockedOrNotReadyWU: number;
  coveragePercent: number; // 0–100
  feasibility: PriorityFeasibility;
  // v22
  hasParticipationRisk: boolean;
  decisionWithParticipation: DecisionWithParticipation;
  /**
   * Why this priority is Blocked, in the engine's own words — e.g. the package
   * or competing set that stopped it. Null when nothing is blocked. Optional:
   * runs persisted before this field existed do not carry it.
   */
  blockedReason?: string | null;
}

export interface ResourceSummary {
  resourceType: string;
  capacityWU: number;
  demandWU: number;
  remainingWU: number;
  utilizationPercent: number; // 0–100+
}

export interface ExecutiveSummary {
  totalWorkUnits: number;
  includedCount: number;
  deferredCount: number;
  blockedCount: number;
  notReadyCount: number;
  needsSizingCount: number;
  noCapacityModelCount: number;
  /**
   * Deferred because the pool had room but nobody qualified held it (Phase B).
   * Optional: runs persisted before qualified capacity shipped do not carry it.
   */
  noQualifiedCapacityCount?: number;
  overallFeasibility: PriorityFeasibility;
  // v22
  participationAtRiskCount: number;
  participationBlockedCount: number;
}

// ---------------------------------------------------------------------------
// Warnings
// ---------------------------------------------------------------------------

export interface PlanningWarning {
  code: string;
  message: string;
  workUnitId?: string;
  priorityId?: string;
}

// ---------------------------------------------------------------------------
// Cleansed work item (PRD §5.3)
// ---------------------------------------------------------------------------

export interface CleansedWorkItem {
  originalWorkUnit: WorkUnit;
  effectiveEffortHours: number; // countedEffortHours ?? effortEstimateHours
  workUnitsRequired: number;
  resourceType: string;
}

// ---------------------------------------------------------------------------
// Engine I/O
// ---------------------------------------------------------------------------

/**
 * A relationship group the plan has to honour, flattened to priority ids.
 *
 * Deliberately not the Prioritization `ItemRelationshipGroup` shape: the engine
 * should not know about membership roles, statuses or edge vocabularies. The
 * adapter translates; the engine only needs "these priorities are bound
 * together, under this rule."
 */
export interface PlanningRelationshipGroup {
  id: string;
  /**
   * package    = all-or-nothing.
   * exclusive  = at most one (Competing, and possibility spectrums).
   * duplicate  = at most one, because the members are the same underlying need
   *              (a confirmed Cluster). Same arithmetic as exclusive, different
   *              thing to tell the user — one is a choice between options, the
   *              other is counting one piece of work twice.
   */
  rule: 'package' | 'exclusive' | 'duplicate';
  /** Human label for the decision reason, e.g. the group's name. */
  label: string | null;
  memberPriorityIds: string[];
}

// ---------------------------------------------------------------------------
// Qualified capacity (v23, Phase B) — capability, credentials, fungibility
// ---------------------------------------------------------------------------

/**
 * One atom of supply: a role assignment's hours, plus what the resource holding
 * them can actually do.
 *
 * A manual capacity row also becomes a supply unit, with `attributable: false`.
 * Nobody stands behind those hours, so they can satisfy work that asks for
 * nothing in particular and no requirement at all — which is the honest answer,
 * not a permissive one.
 */
export interface QualifiedSupplyUnit {
  /** Role assignment id, or `manual:<capacity row id>`. */
  id: string;
  resourceId: string | null;
  resourceName: string;
  resourceType: string;
  /** Work units this unit contributes, already buffered and pro-rated. */
  workUnitCapacity: number;
  /** capability_id → the highest level this resource holds it at. */
  capabilityLevels: Record<string, number>;
  /** credential_id → expiry (ISO date) or null for one that never expires. */
  credentials: Record<string, string | null>;
  /** False for manual pool rows — hours no named resource stands behind. */
  attributable: boolean;
  /**
   * Which allocation loop may draw on this unit.
   *
   * Roster units are 'any': the same people back both grains, which is exactly
   * what makes a Hybrid run — where work units and undecomposed priorities are
   * planned side by side — compete for the same hours instead of each getting a
   * private copy of them. A manual pool row carries the grain of the table it
   * came from (planning_capacity is work-unit grain,
   * planning_priority_capacity is priority grain), mirroring which loop reads
   * it today.
   */
  grain: 'work_unit' | 'priority' | 'any';
  /** Reserved capacity: only satisfies this priority (Phase C). */
  dedicatedToPriorityId: string | null;
}

export type RequirementDimension = 'capability' | 'credential' | 'named_resource';

/** One thing a priority or work unit needs from whoever does it. */
export interface PlanningRequirement {
  id: string;
  /** Exactly one grain is set. */
  priorityId: string | null;
  workUnitId: string | null;
  dimension: RequirementDimension;
  capabilityId: string | null;
  /** Resolved name, so an exclusion reason can say it out loud. */
  capabilityName: string | null;
  minLevel: number | null;
  credentialId: string | null;
  credentialName: string | null;
  namedResourceId: string | null;
  namedResourceName: string | null;
}

/**
 * The qualification substrate for a run.
 *
 * Absent means anonymous pools: exactly today's arithmetic, which stays a fully
 * supported mode forever — the correct fidelity for an early viability pass and
 * for a workspace that will never model people.
 */
export interface QualificationInput {
  supply: QualifiedSupplyUnit[];
  requirements: PlanningRequirement[];
  /** End of the scenario horizon (ISO date), for credential expiry. */
  horizonEndDate: string | null;
}

export interface PlanningEngineInput {
  scenario: Scenario;
  priorities: Priority[];
  workUnits: WorkUnit[];
  dependencies: Dependency[];
  priorityDependencies: PriorityDependency[];
  capacityRows: CapacityModelRow[];
  priorityCapacityRows: PriorityCapacityRow[];
  teamParticipation: TeamParticipation[];
  /**
   * Package / Competing groups touching the scoped priorities. Optional so
   * existing callers and fixtures keep working; absent means "no group rules
   * to honour", which is the pre-existing behaviour.
   */
  relationshipGroups?: PlanningRelationshipGroup[];
  /**
   * Uncertainty multiplier per execution recipe id, for scenarios whose
   * uncertaintyBasis is 'work_type'. Resolved by the caller because this module
   * is pure. Absent, or a 'buffer' scenario, means no demand adjustment.
   */
  recipeMultipliers?: ReadonlyMap<string, number>;
  /**
   * Roster-qualified supply and the requirements profile on the demand side
   * (Phase B). Optional by construction: absent means the qualified pool is the
   * whole pool, and every decision is byte-identical to a pre-Phase-B run.
   */
  qualification?: QualificationInput;
}

export interface PlanningEngineOutput {
  scenario: Scenario;
  planningMode: PlanningMode;
  // Work-unit grain output (used in Work Units and Hybrid modes, also as detail for Priority mode)
  planRecords: PlanRecord[];
  // Priority grain output (used in Priority Estimate and Hybrid modes)
  priorityPlanRecords: PriorityPlanRecord[];
  prioritySummaries: PrioritySummary[];
  resourceSummaries: ResourceSummary[];
  executiveSummary: ExecutiveSummary;
  warnings: PlanningWarning[];
  /**
   * Two-lines/three-bands read model (v23). Present only when the run was
   * executed through `runPlanningEngineWithBands`; `runPlanningEngine` itself
   * stays a single deterministic pass and never sets this.
   */
  viabilityBands?: ViabilityBandReadModel;
  generatedAt: string;
}

// ---------------------------------------------------------------------------
// Viability bands (v23) — two lines, three bands
// ---------------------------------------------------------------------------

/**
 * Where a priority sits relative to the two planning lines.
 *
 * The binary `isAboveLine` answer collapses "we can definitely do this" and
 * "we can probably do this" into one side of one line. Bands separate them by
 * running the same pure engine twice — once optimistically (raw estimates,
 * unbuffered pools) and once conservatively (uncertainty-adjusted estimates,
 * buffered pools) — and reading the pair.
 */
export type ViabilityBand = 'firm' | 'at_risk' | 'out';

export const VIABILITY_BAND_LABELS: Record<ViabilityBand, string> = {
  firm: 'In — firm',
  at_risk: 'In — at risk',
  out: 'Out',
};

export interface ViabilityBandRecord {
  priorityId: string;
  band: ViabilityBand;
  /** Why this priority is not firm. Empty string for firm. */
  bandReason: string;
  conservativeFeasibility: PriorityFeasibility;
  optimisticFeasibility: PriorityFeasibility;
  conservativeCoveragePercent: number;
  optimisticCoveragePercent: number;
  /** Commitment size in work units — the magnitude channel for the band view. */
  requiredWU: number;
  /**
   * The engine's own reason for not carrying this, lifted from the plan record
   * so the leadership view can say WHY at the resolution the record already
   * knows — e.g. which capability, at what level, and how much qualified pool
   * was left — instead of a generic band label. Null when nothing excluded it.
   */
  exclusionReason: string | null;
  /**
   * How far this item sits below the one ranked immediately above it, 0–1,
   * normalised against the largest adjacent gap in the ranked set. 0 when the
   * ranked list carries no scores to measure distance with, which renders as
   * today's uniform spacing.
   */
  rankSeparation: number;
}

export interface ViabilityBandReadModel {
  records: ViabilityBandRecord[];
  firmCount: number;
  atRiskCount: number;
  outCount: number;
  /**
   * Demand multiplier applied to the conservative pass. 1.0 until work-type
   * uncertainty multipliers reach the engine (recipes PRD Phase 1); at 1.0 the
   * conservative pass *is* the primary run and no third pass is executed.
   */
  conservativeDemandMultiplier: number;
  /** The scenario capacity buffer the optimistic pass removed, 0–1. */
  capacityBufferPercent: number;
  /** Which upstream score the rank-distance channel was measured from. */
  rankDistanceBasis: 'valueScore' | 'costOfDelay' | 'impactScore' | 'none';
  generatedAt: string;
}
